/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import React from 'react';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { noopUndefined } from 'src/core/utils/base';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { ConfusionRecord } from 'src/customText/core/services/modelGuidance/customTextModelGuidanceSwaggers';
import { ModelGuidanceCardMessages as messages } from 'src/modules/modelGuidance/components/modelGuidanceCardMessages';
import { ModelGuidanceCardKey } from 'src/modules/modelGuidance/components/tabs/OverviewTab';
import { getIndefinite, handleGetLabel, SchemaType } from 'src/modules/modelGuidance/modelGuidanceUtils/modelGuidanceUtils';
import { ModelGuidanceStore } from 'src/modules/modelGuidance/stores/ModelGuidanceStore';

type UseModelGuidanceCardsDataProps = {
    pivotName?: SchemaType;
    isExtractionProject: boolean;
    modelGuidanceStore: ModelGuidanceStore;
    handleViewDetailsClick?: (cardKey: ModelGuidanceCardKey) => void;
};

type GuidanceCardData = {
    labels: string[];
    errorMessage: string;
    rightMessage: string;
    numberOfIssues: number;
    errorDescription: string;
    shortErrorMessage: string;
    shortRightMessage: string;
    issuesNumberMessage: string;
    recommendationTitle: string;
    descriptionLinkText: string;
    cardKey: ModelGuidanceCardKey;
    onViewDetailsClick: () => void;
    recommendationDescription: string;
    tabKeyToRedirectOnLinkClick: string;
};

export const useModelGuidanceCardsData = (props: UseModelGuidanceCardsDataProps) => {
    const { modelGuidanceStore, handleViewDetailsClick = noopUndefined, pivotName } = props;

    const { localize } = useLocalization();
    const { serviceName } = getPortalFromUrl();

    const { labelType, capitalizedLabelName, nonSpecificLabelType } = React.useMemo(
        () => ({
            labelType: handleGetLabel(pivotName, serviceName, false),
            capitalizedLabelName: handleGetLabel(pivotName, serviceName, true),
            nonSpecificLabelType: `${localize(getIndefinite(pivotName, serviceName))} ${localize(
                handleGetLabel(pivotName, serviceName, false)
            )}`
        }),
        [serviceName, pivotName]
    );

    const getUnbalancedLabeled = (trainLabels: string[], testLabels: string[]) => [
        ...trainLabels.map(label => `"${label}" ${localize(messages.inTrainingSet)}`),
        ...testLabels.map(label => `"${label}" ${localize(messages.inTestSet)}`)
    ];

    const getConfusedLabeledPairs = (confusedLabels: ConfusionRecord[]): string[] =>
        confusedLabels.map(record => `"${Object.keys(record)[0]}" ${localize(messages.mayBeSimilar)} "${Object.values(record)[0]}"`);

    const modelGuidanceCardsInfo = React.useMemo(
        (): { [key: string]: GuidanceCardData } =>
            modelGuidanceStore
                ? {
                      'not-enough-data-card': {
                          cardKey: 'not-enough-data-card',
                          labels: modelGuidanceStore.insufficientExamples,
                          errorMessage: pivotName
                              ? localize(messages.notEnoughLabelsInTrainingSet, {
                                    labelType: localize(labelType)
                                })
                              : localize(messages.notEnoughDataError),
                          rightMessage: localize(messages.notEnoughDataRight),
                          tabKeyToRedirectOnLinkClick: 'data-distribution-tab',
                          shortErrorMessage: pivotName
                              ? localize(messages.notEnoughLabelsInTrainingSet, {
                                    labelType: localize(labelType)
                                })
                              : localize(messages.notEnoughDataError),
                          shortRightMessage: localize(messages.notEnoughDataRight),
                          numberOfIssues: modelGuidanceStore.insufficientExamples.length,
                          descriptionLinkText: localize(messages.trainingSetDataDistributionLink),
                          onViewDetailsClick: () => handleViewDetailsClick('not-enough-data-card'),
                          errorDescription:
                              pivotName === 'Entities'
                                  ? localize(messages.notEnoughEntitiesInTrainingSetRecommendation)
                                  : pivotName === 'Intents'
                                  ? localize(messages.notEnoughIntentsInTrainingSetRecommendation)
                                  : localize(messages.notEnoughDataErrorDescription, {
                                        nonSpecificLabelType
                                    }),
                          recommendationTitle: localize(messages.notEnoughDataRecommendationTitle, {
                              labelType: localize(capitalizedLabelName)
                          }),
                          issuesNumberMessage: localize(messages.notEnoughDataIssuesNumberMessage, {
                              labelType: localize(labelType),
                              issuesNumber: modelGuidanceStore.insufficientExamples.length
                          }),
                          recommendationDescription: localize(messages.notEnoughDataRecommendationDescription, {
                              labelType: localize(labelType),
                              type: localize(pivotName ? messages.utterances : messages.documents)
                          })
                      },
                      'missing-labels-card': {
                          cardKey: 'missing-labels-card',
                          labels: modelGuidanceStore.missingLabels,
                          tabKeyToRedirectOnLinkClick: 'data-distribution-tab',
                          numberOfIssues: modelGuidanceStore.missingLabels.length,
                          errorMessage: localize(messages.missingLabelsError, { labelType: localize(labelType) }),
                          rightMessage: localize(messages.missingLabelsRight, { labelType: localize(labelType) }),
                          descriptionLinkText: localize(messages.testSetDataDistributionLink),
                          shortErrorMessage: localize(messages.missingLabelsError, { labelType: localize(labelType) }),
                          shortRightMessage: localize(messages.missingLabelsRight, { labelType: localize(labelType) }),
                          onViewDetailsClick: () => handleViewDetailsClick('missing-labels-card'),
                          issuesNumberMessage: localize(messages.missingLabelsIssuesNumberMessage, {
                              labelType: localize(labelType),
                              issuesNumber: modelGuidanceStore.missingLabels.length
                          }),
                          recommendationDescription: localize(messages.missingLabelRecommendationDescription, {
                              labelType: localize(labelType),
                              type: localize(pivotName ? messages.utterances : messages.documents)
                          }),
                          errorDescription: localize(messages.missingLabelsErrorDescription, { nonSpecificLabelType }),
                          recommendationTitle: localize(messages.missingLabelRecommendationTitle, {
                              labelType: localize(capitalizedLabelName)
                          })
                      },
                      'unbalanced-labels-card': {
                          cardKey: 'unbalanced-labels-card',
                          tabKeyToRedirectOnLinkClick: 'data-distribution-tab',
                          descriptionLinkText: localize(messages.dataDistributionLink),
                          errorMessage: localize(messages.unbalancedLabelsError, { labelType: localize(labelType) }),
                          errorDescription: localize(messages.unbalancedLabelsErrorDescription, {
                              labelType: localize(labelType),
                              nonSpecificLabelType
                          }),
                          recommendationTitle: localize(messages.unbalancedLabelsRecommendationTitle, {
                              labelType: localize(labelType)
                          }),
                          onViewDetailsClick: () => handleViewDetailsClick('unbalanced-labels-card'),
                          shortErrorMessage: localize(messages.unbalancedLabelsShortError, { labelType: localize(labelType) }),
                          recommendationDescription: localize(messages.unbalancedLabelsRecommendationDescription, {
                              labelType: localize(labelType),
                              type: localize(pivotName ? messages.utterances : messages.documents)
                          }),
                          rightMessage: localize(messages.unbalancedLabelsRight, { labelType: localize(capitalizedLabelName) }),
                          shortRightMessage: localize(messages.unbalancedLabelsShortRight, { labelType: localize(capitalizedLabelName) }),
                          labels: getUnbalancedLabeled(modelGuidanceStore.trainUnbalancedLabels, modelGuidanceStore.testUnbalancedLabels),
                          numberOfIssues: modelGuidanceStore.testUnbalancedLabels.length + modelGuidanceStore.trainUnbalancedLabels.length,
                          issuesNumberMessage: localize(messages.unbalancedLabelsIssuesNumberMessage, {
                              labelType: localize(labelType),
                              issuesNumber: modelGuidanceStore.testUnbalancedLabels.length + modelGuidanceStore.trainUnbalancedLabels.length
                          })
                      },
                      'uneven-distribution-card': {
                          cardKey: 'uneven-distribution-card',
                          tabKeyToRedirectOnLinkClick: 'data-distribution-tab',
                          descriptionLinkText: localize(messages.dataDistributionLink),
                          numberOfIssues: modelGuidanceStore.unbalancedDataDistribution ? 1 : 0,
                          labels: modelGuidanceStore.unbalancedDataDistribution
                              ? [localize(messages.trainingSet), localize(messages.testSet)]
                              : [],
                          errorMessage: localize(messages.unEvenDistributionError, { labelType: localize(labelType) }),
                          rightMessage: localize(messages.unEvenDistributionRight, { labelType: localize(capitalizedLabelName) }),
                          errorDescription: localize(messages.unEvenDistributionErrorDescription, {
                              labelType: localize(labelType)
                          }),
                          onViewDetailsClick: () => handleViewDetailsClick('uneven-distribution-card'),
                          recommendationTitle: localize(messages.unEvenDistributionRecommendationTitle, {
                              labelType: localize(labelType)
                          }),
                          issuesNumberMessage: localize(messages.unEvenDistributionIssuesNumberMessage, {
                              labelType: localize(capitalizedLabelName)
                          }),
                          shortErrorMessage: localize(messages.unEvenDistributionShortError, { labelType: localize(labelType) }),
                          recommendationDescription: localize(messages.unEvenDistributionRecommendationDescription, {
                              labelType: localize(labelType),
                              type: localize(pivotName ? messages.utterances : messages.documents)
                          }),
                          shortRightMessage: localize(messages.unEvenDistributionShortRight, { labelType: localize(capitalizedLabelName) })
                      },
                      'confusion-labels-card': {
                          cardKey: 'confusion-labels-card',
                          tabKeyToRedirectOnLinkClick: 'confusion-matrix-tab',
                          descriptionLinkText: localize(messages.confusionMatrix),
                          numberOfIssues: modelGuidanceStore.confusionLabels.length,
                          labels: getConfusedLabeledPairs(modelGuidanceStore.confusionLabels),
                          errorMessage: localize(messages.unClearDistinctionError, { labelType: localize(labelType) }),
                          rightMessage: localize(messages.unClearDistinctionRight, { labelType: localize(capitalizedLabelName) }),
                          onViewDetailsClick: () => handleViewDetailsClick('confusion-labels-card'),
                          recommendationTitle: localize(messages.unClearDistinctionRecommendationTitle, {
                              labelType: localize(capitalizedLabelName)
                          }),
                          issuesNumberMessage: localize(messages.unClearDistinctionIssuesNumberMessage, {
                              labelType: localize(labelType),
                              issuesNumber: modelGuidanceStore.confusionLabels.length
                          }),
                          shortErrorMessage: localize(messages.unClearDistinctionShortError, { labelType: localize(labelType) }),
                          shortRightMessage: localize(messages.unClearDistinctionShortRight, { labelType: localize(capitalizedLabelName) }),
                          errorDescription: localize(messages.unClearDistinctionErrorDescription, { labelType: localize(labelType) }),
                          recommendationDescription: localize(messages.unClearDistinctionRecommendationDescription, {
                              labelType: localize(labelType)
                          })
                      }
                  }
                : {},
        [
            labelType,
            modelGuidanceStore,
            nonSpecificLabelType,
            modelGuidanceStore?.missingLabels,
            modelGuidanceStore?.confusionLabels,
            modelGuidanceStore?.testUnbalancedLabels,
            modelGuidanceStore?.insufficientExamples,
            modelGuidanceStore?.trainUnbalancedLabels,
            modelGuidanceStore?.unbalancedDataDistribution
        ]
    );

    const modelGuidanceCardArray = React.useMemo(
        (): GuidanceCardData[] =>
            modelGuidanceStore && modelGuidanceCardsInfo
                ? [
                      modelGuidanceCardsInfo['not-enough-data-card'],
                      modelGuidanceCardsInfo['missing-labels-card'],
                      modelGuidanceCardsInfo['unbalanced-labels-card'],
                      modelGuidanceCardsInfo['confusion-labels-card'],
                      modelGuidanceCardsInfo['uneven-distribution-card']
                  ]
                : [],
        [modelGuidanceStore, modelGuidanceCardsInfo]
    );

    const modelGuidanceCardBladeHeader = React.useMemo(
        (): { [key: string]: string } =>
            modelGuidanceStore
                ? {
                      'improve-model': localize(messages.improveModelTitle),
                      'missing-labels-card':
                          modelGuidanceStore.missingLabels?.length > 0
                              ? localize(messages.missingLabelsError, { labelType: localize(labelType) })
                              : localize(messages.missingLabelsRight, { labelType: localize(labelType) }),
                      'not-enough-data-card': localize(
                          modelGuidanceStore.insufficientExamples?.length > 0 ? messages.notEnoughDataError : messages.notEnoughDataRight
                      ),
                      'unbalanced-labels-card': localize(
                          modelGuidanceStore.testUnbalancedLabels?.length + modelGuidanceStore.trainUnbalancedLabels?.length > 0
                              ? messages.unbalancedLabelsError
                              : messages.unbalancedLabelsRight,
                          { labelType: localize(capitalizedLabelName) }
                      ),
                      'confusion-labels-card': localize(
                          modelGuidanceStore.confusionLabels.length > 0
                              ? messages.unClearDistinctionError
                              : messages.unClearDistinctionRight,
                          { labelType: localize(capitalizedLabelName) }
                      ),
                      'uneven-distribution-card': localize(
                          modelGuidanceStore.unbalancedDataDistribution
                              ? messages.unEvenDistributionError
                              : messages.unEvenDistributionRight,
                          { labelType: localize(capitalizedLabelName) }
                      )
                  }
                : { 'improve-model': localize(messages.improveModelTitle) },
        [
            modelGuidanceStore,
            modelGuidanceStore?.missingLabels,
            modelGuidanceStore?.confusionLabels,
            modelGuidanceStore?.insufficientExamples,
            modelGuidanceStore?.testUnbalancedLabels,
            modelGuidanceStore?.trainUnbalancedLabels,
            modelGuidanceStore?.unbalancedDataDistribution
        ]
    );

    return { modelGuidanceCardsInfo, modelGuidanceCardBladeHeader, modelGuidanceCardArray };
};
